import { AddToQueue, MoreVert } from '@mui/icons-material';
import { Divider, Menu as MuiMenu } from '@mui/material';
import type { ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteChannel } from '~/api/channels/delete';
import { IconButton } from '~/components/IconButton';
import { ActionButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreAction,
  MoreDeleteAction,
  mergeGridActionProps,
  type GridActionCellProps,
} from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import type { ChannelList__Channel as Channel } from '../../queries/list.generated';

export interface MenuProps extends GridActionCellProps {
  buttonType?: 'action' | 'icon';
  channel: Channel;
  Icon?: ElementType;
  onAddToGuideClick: (channel: Channel) => void;
}

export const Menu = ({
  buttonType = 'icon',
  channel,
  Icon = MoreVert,
  onAddToGuideClick,
  ...cellProps
}: MenuProps) => {
  const { t } = useTranslation();
  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<typeof channel>();
  const channelActionProps = mergeGridActionProps(moreTableActionProps(channel), cellProps);
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [deleteChannel] = useDeleteChannel();

  return (
    <>
      {buttonType === 'icon' ? (
        <IconButton
          label={t('pages.channels.list.columns.actions')}
          onClick={channelActionProps.onClick}
          size="small"
        >
          <Icon />
        </IconButton>
      ) : (
        <ActionButton {...channelActionProps} />
      )}
      <MuiMenu {...moreMenuProps}>
        <MoreAction
          {...moreActionProps}
          Icon={AddToQueue}
          title={t('pages.channels.actions.add_to_guide')}
          onClick={() => {
            assert(moreMenuProps.context != null, 'add: no device context');
            onAddToGuideClick(channel);
          }}
        />

        <Divider />
        <MoreDeleteAction
          {...moreActionProps}
          tooltip={
            moreMenuProps.context?.canDestroy.value
              ? ''
              : moreMenuProps.context?.canDestroy.reasons?.fullMessages.join(', ') ||
                t('pages.channels.list.menu.cannot_delete_tooltip')
          }
          disabled={!moreMenuProps.context?.canDestroy.value}
          onClick={async () => {
            assert(moreMenuProps.context !== undefined);
            if (!(await confirmDelete())) return;
            await deleteChannel({
              variables: {
                input: { channelIds: [moreMenuProps.context.id] },
              },
            });
            moreMenuProps.onClose();
          }}
        />
      </MuiMenu>

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.dialogs.permanently_delete')}
        deleteConfirm
        prompt={t('pages.channels.dialogs.delete.prompt')}
        title={t('pages.channels.dialogs.delete.title')}
      />
    </>
  );
};
